/**
 * SPEC-TrainingSimulator §8.2 / §8.3 / §8.3a — the item screen (all phases).
 *
 *   /train/run/:attemptId     the trainee's attempt: GET next → lesson | item | review | done
 *   /train/preview/:itemId    Senior/Admin preview of one item (§3): same screen,
 *                             graded by POST /preview/…/answer, nothing recorded
 *
 * Two layouts, one screen (Phase C):
 *   Phases 1–2  clone left, the narrow question-rail pane right. Phase 1 grades
 *               each question as it is answered; Phase 2 records silently and
 *               shows everything at once on the review screen (D18).
 *   Phases 3–4  no rail: the clone goes FULL WIDTH and the real DecisionFooter
 *               sits in a sticky bottom slot, with the real AgentResolutionPanel
 *               replacing it on Bad — the live agent page's own arrangement, so
 *               the certification screen is the working screen (D1).
 *
 * Title row: ← Exit · Training strip (phase · 4-segment progress · n of N ·
 * rule chip · clock chip + "+ Time" in phases 2–4) · inert CU Notified / Skip.
 * Body: the SAME delivery clone the key editor shows (SimulatorDeliveryView →
 * ChecklistCard, CompletionDetailsCard, PhotoBoxPanel, TextHighlightSurface)
 * fed from the served traineeSnapshot.
 *
 * Layout (2026-09-09): the rail pane is narrow (max 420px) so the clone — and
 * with it the POD photo — keeps the width it has on the live agent page, and
 * the Q1–Q10 progress is a horizontal chip strip ABOVE the question card
 * instead of a vertical list beside it, so a ✓/✗ appears right under Check
 * answer. The two right-hand blocks are aligned to the clone's own rows
 * (progress ↔ hero card, question card ↔ Delivery Details / Photos) by
 * measuring the hero: `useCloneHeroAlign` watches its offset and height, so
 * the alignment holds when the hero's badge row wraps or the window resizes.
 *
 * Outbound links are inert for trainees (`inertLinks`): "View in Onfleet" (and
 * any future Slack / Podium button) is greyed and does not navigate — a
 * trainee has no Onfleet or Podium access yet.
 *
 * Key pointers are hidden until answered (2026-09-09): the served payload has
 * no boxes / highlights / valueHighlight. They arrive in the graded response
 * for that question and are then kept in `graded[qid].reveal`, so they stay
 * drawn for the rest of the item. What is outlined is the KEY's answer (Sarah,
 * 2026-09-11): for q1/q2/q7 the answer's own anchor, or — for a key saved
 * before highlight capture existed — the keyed value located in the card text
 * (`keyedAnswerHighlight`). The trainee's own pick is stated in the rail
 * ("You answered") and is not outlined, so a green outline always means
 * "this is the right text".
 *
 * Clocks (§8.4): the chip counts down locally from the server's reading, but
 * every decision about it is the server's — /extend and /timeout both recompute
 * elapsed from `opened_at`, so leaving the tab (or the page) does not pause it.
 * Exiting phases 3–4 says so before it lets go.
 *
 * Isolation (§8.5): no claim, no socket, no live delivery query. The only
 * network calls are the runner hooks in simulator-runner-queries.ts.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams, useSearch } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Starling } from "@/lib/starling-brand";
import { SimulatorDeliveryView } from "@/components/simulator/delivery-clone";
import { boxLayerLabel, evidenceOptionsFor } from "@/components/simulator/question-controls";
import { QuestionRail, type RailGraded } from "@/components/simulator/question-rail";
import { RunClock } from "@/components/simulator/run-clock";
import { RunDecisionFooter, type ResolutionSave } from "@/components/simulator/run-decision-footer";
import { ReviewScreen } from "@/components/simulator/review-screen";
import { LessonView } from "@/pages/train-lesson";
import type { PhotoBoxLayer, PhotoPick } from "@/components/simulator/photo-box-panel";
import type { HighlightLayer } from "@/components/simulator/text-highlight";
import {
  RUN_KEYS, useAnswerQuestion, useAttemptNext, useAttemptReview, useDecideItem, useExtendTime, useMarkLessonRead,
  usePreviewAnswer, usePreviewItem, useTimeoutItem,
  type GradedQuestion, type RunnerNext,
} from "@/lib/simulator-runner-queries";
import {
  foldResolutionNotes, initialActiveId, keyedAnswerHighlight, nextActiveId, phaseRuleChip, popHistory, pushHistory,
  skippedQuestionIds, toggleWhere, type DraftHistory,
} from "@/lib/simulator-runner-helpers";
import { PHASE_LABELS, whereFieldActive, type AnswerEntry, type QuestionId, type TextHighlight } from "@shared/simulator-helpers";
import { useQueryClient } from "@tanstack/react-query";

const T = {
  bg: "#F7F8FA",
  card: "#FFFFFF",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  sub: "#F3F4F6",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.10)",
  good: "#166534",
  goodBg: "#DCFCE7",
  amber: "#B45309",
  amberBg: "#FEF3C7",
  slate: "#334155",
} as const;

const COMPLETION_NOTE_ELEMENT_ID = "simulator-run-completion-note";
const DELIVERY_DETAILS_ELEMENT_ID = "simulator-run-delivery-details";

export default function TrainRun() {
  const params = useParams<{ attemptId?: string; itemId?: string }>();
  const search = useSearch();
  const preview = !!params.itemId;
  // §12 Q5: a Senior may ask for a specific phase's screen; without this the
  // documented `?phase=` never left the browser (audit M4).
  const previewPhase = useMemo(() => {
    const raw = Number(new URLSearchParams(search).get("phase"));
    return Number.isInteger(raw) && raw >= 1 && raw <= 4 ? raw : null;
  }, [search]);
  const attemptQ = useAttemptNext(preview ? null : params.attemptId);
  const previewQ = usePreviewItem(preview ? params.itemId : null, previewPhase);
  const q = preview ? previewQ : attemptQ;
  const markRead = useMarkLessonRead();
  const qc = useQueryClient();

  if (q.isLoading) return <div style={{ padding: 24, fontFamily: Starling.fontBody }}>Loading delivery…</div>;
  if (q.isError || !q.data) {
    return (
      <div style={{ padding: 24, fontFamily: Starling.fontBody }}>
        Couldn't load this {preview ? "item" : "attempt"}. <Link href={preview ? "/train/manage" : "/train"} style={{ color: T.purple }}>Back</Link>
      </div>
    );
  }
  const data: RunnerNext = q.data;

  if (data.kind === "lesson") {
    const onContinue = async () => {
      try { await markRead.mutateAsync(data.lesson.id); } finally { qc.invalidateQueries({ queryKey: RUN_KEYS.next(params.attemptId ?? "") }); }
    };
    return <LessonView lesson={data.lesson} onContinue={onContinue} pending={markRead.isPending} exitHref="/train" />;
  }

  // Phase 2 (D18): the phase is finished and the review screen is due.
  if (data.kind === "review") return <ReviewPage attemptId={params.attemptId ?? ""} />;

  if (data.kind === "done") {
    return (
      <div data-testid="train-run-done" style={{ padding: "40px 22px", fontFamily: Starling.fontBody, color: T.text, maxWidth: 640 }}>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "22px 24px" }}>
          <div style={{ fontFamily: Starling.fontHead, fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Phase {data.attempt.phase} · {PHASE_LABELS[data.attempt.phase]} complete ✓</div>
          <div style={{ fontSize: 13.5, color: T.txt2, marginBottom: 16 }}>Nice work. Your Senior can see how it went; head back to Training for what's next.</div>
          <Link href="/train" data-testid="train-run-home" style={{ display: "inline-block", padding: "9px 20px", borderRadius: 8, fontWeight: 700, fontSize: 13, background: T.purple, color: "#fff", textDecoration: "none" }}>Back to Training</Link>
        </div>
      </div>
    );
  }

  return <ItemScreen key={data.item.id} data={data} preview={preview} attemptId={params.attemptId ?? null} previewItemId={params.itemId ?? null} />;
}

/** §8.3 / D18 — everything Phase 2 held back, at once. "Continue to Phase 3" dismisses. */
function ReviewPage({ attemptId }: { attemptId: string }) {
  const [, navigate] = useLocation();
  const q = useAttemptReview(attemptId || null);
  if (q.isLoading) return <div style={{ padding: 24, fontFamily: Starling.fontBody }}>Loading your review…</div>;
  if (q.isError || !q.data) {
    return (
      <div style={{ padding: 24, fontFamily: Starling.fontBody }}>
        Couldn't load the review. <Link href="/train" style={{ color: T.purple }}>Back to Training</Link>
      </div>
    );
  }
  return (
    <ReviewScreen
      review={q.data}
      questions={q.data.questions}
      evidence={(item) => [
        ...Array.from({ length: item.evidence.photoCount }, (_, i) => ({ value: `photo:${i + 1}`, label: `Photo ${i + 1}` })),
        ...(item.evidence.hasCompletionNote ? [{ value: "note:completion", label: "Completion note" }] : []),
      ]}
      onContinue={() => navigate("/train")}
    />
  );
}

/**
 * Row alignment for the right-hand pane: the offset of the clone's hero card
 * from the top of the body grid (the breadcrumb above it) and the hero's own
 * height. The pane pads itself by the first and sizes the progress strip with
 * the second, so the two columns read as the same two rows. Measured rather
 * than hard-coded because the hero grows when its badge row wraps.
 */
function useCloneHeroAlign(rootRef: React.RefObject<HTMLDivElement>, itemId: string) {
  const [align, setAlign] = useState({ offset: 0, heroHeight: 0 });
  useEffect(() => {
    const root = rootRef.current;
    const hero = root?.querySelector<HTMLElement>('[data-testid="clone-hero"]');
    if (!root || !hero) return;
    const measure = () => {
      const offset = Math.max(0, Math.round(hero.getBoundingClientRect().top - root.getBoundingClientRect().top));
      const heroHeight = Math.round(hero.getBoundingClientRect().height);
      setAlign((prev) => (prev.offset === offset && prev.heroHeight === heroHeight ? prev : { offset, heroHeight }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(hero);
    ro.observe(root);
    return () => ro.disconnect();
  }, [rootRef, itemId]);
  return align;
}

// ─── Item screen ─────────────────────────────────────────────────────────────

type ItemData = Extract<RunnerNext, { kind: "item" }>;

function ItemScreen({ data, preview, attemptId, previewItemId }: { data: ItemData; preview: boolean; attemptId: string | null; previewItemId: string | null }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const answerM = useAnswerQuestion();
  const previewM = usePreviewAnswer();
  const decideM = useDecideItem();
  const extendM = useExtendTime();
  const timeoutM = useTimeoutItem();
  const phase = data.phase ?? data.attempt?.phase ?? 1;
  const railPhase = phase <= 2;
  // Phase 2 records silently (§8.3): no grade, no feedback card, no reveal
  // until the review screen. Phase 1 is the instant-feedback phase.
  const instantFeedback = phase === 1;

  // Graded questions: seeded from the resume payload, extended by each Check
  // answer. Phase 1 only — the server sends no grade for the other phases, and
  // seeding this regardless of phase is what turned a Phase 2 reload into a
  // feedback card and a read-only lock (AUDIT-PhaseC H1).
  const [graded, setGraded] = useState<Partial<Record<QuestionId, RailGraded>>>(() => {
    const out: Partial<Record<QuestionId, RailGraded>> = {};
    if (!instantFeedback) return out;
    for (const [qid, g] of Object.entries(data.answered)) out[qid as QuestionId] = { answer: g.answer, grade: g.grade, feedback: g.feedback, reveal: g.reveal };
    return out;
  });
  const [asked, setAsked] = useState<QuestionId[] | null>(data.asked);
  // Phase 2 (silent): what has been sent, with no grade attached. Seeded from
  // the resume payload so an exit and return lands on the same question.
  const [submitted, setSubmitted] = useState<Partial<Record<QuestionId, AnswerEntry>>>(() => {
    const out: Partial<Record<QuestionId, AnswerEntry>> = {};
    for (const [qid, g] of Object.entries(data.answered)) out[qid as QuestionId] = g.answer;
    return out;
  });
  // The rail always lists every phase question; the ones the KEY does not ask
  // (Q8 when the key's Q7 is Yes) are known once Q7 is graded and stay in the
  // rail dimmed, so the count never shifts (audit W1 / W7).
  const questions = data.questions;
  const skipped = useMemo(() => skippedQuestionIds(questions, asked), [questions, asked]);
  const answeredSoFar = instantFeedback ? graded : submitted;
  const [activeId, setActiveId] = useState<QuestionId>(
    () => initialActiveId(questions, data.answered, skippedQuestionIds(questions, data.asked)) ?? questions[0].id,
  );
  const active = questions.find((q) => q.id === activeId) ?? questions[0];

  // Drafts + per-question history (D20 Back = un-pick the last change).
  const [drafts, setDrafts] = useState<Partial<Record<QuestionId, AnswerEntry | null>>>({});
  const [history, setHistory] = useState<DraftHistory>({});
  const onDraftChange = (id: QuestionId, next: AnswerEntry | null) => {
    setHistory((h) => pushHistory(h, id, drafts[id] ?? null));
    setDrafts((d) => ({ ...d, [id]: next }));
  };
  const onBack = () => {
    const popped = popHistory(history, active.id);
    if (!popped) return;
    setHistory(popped.history);
    setDrafts((d) => ({ ...d, [active.id]: popped.draft }));
  };

  const evidence = useMemo(() => evidenceOptionsFor(data.item.snapshot), [data.item.snapshot]);

  // Reveal layers: one per graded question (key pointers, solid when it is the
  // viewed question). The outlined text is the KEY's answer — its own anchor,
  // or the keyed value found in the card text when the key has no anchor.
  const layers = useMemo<Array<PhotoBoxLayer & HighlightLayer>>(() => {
    const out: Array<PhotoBoxLayer & HighlightLayer> = [];
    for (const q of data.questions) {
      const g = graded[q.id];
      if (!g) continue;
      const answer = keyedAnswerHighlight(q.id, g.reveal.keyed, g.reveal.valueHighlight, data.item.snapshot);
      const hl: TextHighlight[] = [...(answer ? [answer] : []), ...g.reveal.highlights];
      out.push({ kind: q.id, label: boxLayerLabel(q.id, q.sort), boxes: g.reveal.boxes, highlights: hl });
    }
    return out;
  }, [graded, data.questions, data.item.snapshot]);
  const activeGraded = !!graded[active.id];

  // Pick mode (§8.3): a "where" field on the active, ungraded question.
  // Phase 2 may reopen and change any earlier question until q10 (§8.3a), so a
  // revisited question comes back pre-filled with what was sent.
  const draft = drafts[active.id] ?? (instantFeedback ? null : submitted[active.id] ?? null);
  const pick: PhotoPick | null = !activeGraded && whereFieldActive(active.id, draft)
    ? {
        selected: ((draft as any)?.where as string[] | undefined) ?? [],
        label: active.text.labels?.where ?? "Where do you see it?",
        onToggle: (target) => {
          const next = toggleWhere((draft as any)?.where as string[] | undefined, target, evidence.map((o) => o.value));
          onDraftChange(active.id, { ...((draft ?? {}) as any), where: next });
        },
      }
    : null;
  const photoTab = active.id === "q6_reference" && (draft as any)?.value === "yes" ? "past" : "pod";

  const onCheck = async () => {
    const answer = (drafts[active.id] ?? {}) as AnswerEntry;
    try {
      const r: GradedQuestion = preview
        ? await previewM.mutateAsync({ itemId: previewItemId!, questionId: active.id, answer })
        : await answerM.mutateAsync({ attemptId: attemptId!, itemId: data.item.id, questionId: active.id, answer });
      if (!instantFeedback) {
        // Phase 2: the server returned {recorded:true}. Mark the chip done and
        // move on — the grade exists only on the server until the review.
        const nextAnswers = { ...submitted, [active.id]: answer };
        setSubmitted(nextAnswers);
        await onNext(nextAnswers);
        return;
      }
      setGraded((g) => ({ ...g, [active.id]: { answer, grade: r.grade, feedback: r.feedback, reveal: r.reveal } }));
      if (r.asked) setAsked(r.asked);
    } catch (e: any) {
      toast({ title: instantFeedback ? "Couldn't check that answer" : "Couldn't save that answer", description: String(e?.message ?? e).replace(/^\d{3}:\s*/, ""), variant: "destructive" });
    }
  };

  // Once this item's decision is recorded it stays "advancing" until the next
  // item mounts (ItemScreen is keyed by item id, so the flag resets itself).
  // Without it the footer re-enabled the moment the POST returned while the
  // OLD item was still on screen, and a fast second click became a second
  // decision for it — 409 "Item already decided", the click lost (Sarah's
  // Phase 4 run, 2026-09-15).
  const [advanced, setAdvanced] = useState(false);
  const onNext = async (answersOverride?: Partial<Record<QuestionId, unknown>>) => {
    const answered = answersOverride ?? answeredSoFar;
    const next = nextActiveId(questions, active.id, answered, skipped);
    if (next) { setActiveId(next); return; }
    // Last question answered → the q10 button click is the item's decision (§8.3).
    const q10 = (instantFeedback ? (graded.q10_decision?.answer as any) : (answered as any).q10_decision) ?? null;
    const decision = (q10?.decision as "GOOD" | "BAD" | undefined) ?? "GOOD";
    if (preview) { navigate(`/train/manage/item/${previewItemId}`); return; }
    if (advanced) return;
    try {
      await decideM.mutateAsync({ attemptId: attemptId!, itemId: data.item.id, decision });
      setAdvanced(true);
      qc.invalidateQueries({ queryKey: RUN_KEYS.next(attemptId!) });
    } catch (e: any) {
      const msg = String(e?.message ?? e).replace(/^\d{3}:\s*/, "");
      if (/already decided/i.test(msg)) { setAdvanced(true); qc.invalidateQueries({ queryKey: RUN_KEYS.next(attemptId!) }); return; }
      toast({ title: "Couldn't move on", description: msg, variant: "destructive" });
    }
  };

  const bodyRef = useRef<HTMLDivElement>(null);
  const align = useCloneHeroAlign(bodyRef, data.item.id);

  const total = data.item.total;
  const index = data.item.index;
  const isLastItem = index + 1 >= total;
  const exitHref = preview ? `/train/manage/item/${previewItemId}` : "/train";

  // ── Phases 3–4: the real footer and the real resolution panel (§8.2, D9) ───
  const [badOpen, setBadOpen] = useState(false);
  const decide = async (body: { decision: "GOOD" | "BAD"; badReason?: string | null; badReasonOther?: string | null; resolutionNotes?: string | null }) => {
    if (preview) { navigate(`/train/manage/item/${previewItemId}`); return; }
    if (advanced) return;
    try {
      await decideM.mutateAsync({ attemptId: attemptId!, itemId: data.item.id, ...(body as any) });
      setAdvanced(true);
      setBadOpen(false);
      qc.invalidateQueries({ queryKey: RUN_KEYS.next(attemptId!) });
    } catch (e: any) {
      const msg = String(e?.message ?? e).replace(/^\d{3}:\s*/, "");
      // Already decided (a timeout beat the click, or a double submit): the
      // item is over either way — move on quietly instead of alarming the trainee.
      if (/already decided/i.test(msg)) { setAdvanced(true); qc.invalidateQueries({ queryKey: RUN_KEYS.next(attemptId!) }); return; }
      toast({ title: "Couldn't record that decision", description: msg, variant: "destructive" });
    }
  };
  const onSaveBad = (input: ResolutionSave) =>
    decide({
      decision: "BAD",
      badReason: input.badReason,
      badReasonOther: input.badReasonOther ?? null,
      // The panel's Step 2 has no column of its own — fold it into the note.
      resolutionNotes: foldResolutionNotes(input),
    });

  const clock = data.clock;
  const onExtend = async () => {
    if (preview || !attemptId) return;
    try { await extendM.mutateAsync({ attemptId, itemId: data.item.id }); } catch (e: any) {
      toast({ title: "Couldn't add time", description: String(e?.message ?? e).replace(/^\d{3}:\s*/, ""), variant: "destructive" });
    }
  };
  // True from the moment the local hard countdown reads 0:00 until the server
  // says otherwise. The footer reads it so a late click cannot race the timeout
  // (AUDIT-PhaseC M1 / L8); the server enforces the same thing regardless.
  const [hardClockOut, setHardClockOut] = useState(false);
  const onTimeout = async () => {
    if (preview || !attemptId) return;
    setHardClockOut(true);
    // The server rules on it: a 409 just means the clock has not really run out,
    // so the footer comes back and the refetch brings the real reading (§8.4).
    try {
      await timeoutM.mutateAsync({ attemptId, itemId: data.item.id });
    } catch {
      setHardClockOut(false);
    }
    qc.invalidateQueries({ queryKey: RUN_KEYS.next(attemptId) });
  };

  // §8.3a — in phases 3–4 the clock keeps running while the trainee is away, so
  // Exit says so before it lets go.
  const exitConfirm = (e: React.MouseEvent) => {
    if (preview || railPhase) return;
    if (!window.confirm("Leave this delivery? The clock keeps running while you are away — it never pauses.")) e.preventDefault();
  };

  return (
    <div data-testid="train-run" data-item-id={data.item.id} data-preview={preview ? "true" : "false"} style={{ padding: "12px 18px 40px", fontFamily: Starling.fontBody, color: T.text, background: T.bg, minHeight: "100%" }}>
      {/* Title row (§8.2) */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <Link href={exitHref} data-testid="train-run-exit" onClick={exitConfirm}
          title={preview ? "Back to the key editor" : railPhase ? "Exit to Training — your progress is saved" : "Exit to Training — the clock keeps running"}
          style={{ width: 28, height: 28, border: `1px solid ${T.border}`, borderRadius: 6, background: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.txt2, textDecoration: "none", fontSize: 15 }}>
          ←
        </Link>
        <div data-testid="training-strip" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 12px", flex: 1 }}>
          <span style={{ fontFamily: Starling.fontHead, fontWeight: 600, fontSize: 13.5, color: T.purple }}>Phase {phase} · {PHASE_LABELS[phase]}</span>
          <span data-testid="training-strip-segments" style={{ display: "inline-flex", gap: 3 }} aria-label={`Phase ${phase} of 4`}>
            {[1, 2, 3, 4].map((p) => <span key={p} style={{ width: 18, height: 6, borderRadius: 3, background: p < phase ? T.good : p === phase ? T.purple : T.border }} />)}
          </span>
          <span data-testid="training-strip-count" style={{ fontSize: 12.5, fontWeight: 700 }}>{index + 1} of {total}</span>
          <span data-testid="training-strip-rule" style={{ fontSize: 11.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: T.purpleSub, color: T.purple }}>
            {phaseRuleChip(phase, clock?.targetSeconds ?? 0, clock?.mode)}
          </span>
          {/* §8.2 — the clock chip and "+ Time" ride in the title row, exactly
              where the live agent page puts them. Phase 1 has no clock at all. */}
          <RunClock
            clock={clock}
            warningSeconds={clock?.warningSeconds ?? 15}
            onExtend={clock?.mode === "extendable" && !preview ? onExtend : undefined}
            extending={extendM.isPending}
            onTimeout={clock?.mode === "hard" && !preview ? onTimeout : undefined}
          />
          {preview && <span data-testid="train-preview-flag" style={{ fontSize: 11.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: T.amberBg, color: T.amber }}>Preview · nothing is recorded</span>}
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
            <button type="button" aria-disabled="true" title="Not available in training" data-testid="clone-cx-notified" style={inert("#EA580C", "#FFEDD5")}>CU Notified</button>
            <button type="button" aria-disabled="true" title="Not available in training" data-testid="clone-skip" style={inert("#fff", T.slate)}>⏭ Skip</button>
          </span>
        </div>
      </div>

      {/* Body. Phases 1–2: clone left at full agent-page proportions, narrow rail
          right. Phases 3–4: no rail, so the clone takes the whole width and the
          real footer sits under it (Sarah's call, 2026-09-09). */}
      <div
        ref={bodyRef}
        style={railPhase ? { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(360px, 420px)", gap: 14 } : { display: "block" }}
        data-testid="train-run-grid"
        data-layout={railPhase ? "rail" : "full"}
      >
        <div>
          <SimulatorDeliveryView
            snapshot={data.item.snapshot}
            completionNoteElementId={COMPLETION_NOTE_ELEMENT_ID}
            deliveryDetailsElementId={DELIVERY_DETAILS_ELEMENT_ID}
            statusLabel={preview ? "Training · preview" : "Training"}
            inertLinks
            pointers={{ layers, activeKind: activeGraded ? active.id : null, activeLabel: activeGraded ? boxLayerLabel(active.id, active.sort) : undefined, onChangeBoxes: () => {}, readOnly: true }}
            photoPick={pick}
            photoTab={photoTab}
          />
          {/* Phases 3–4 footer slot (§8.2): the live DecisionFooter, and the
              live AgentResolutionPanel in its place once Bad is pressed. */}
          {!railPhase && (
            <RunDecisionFooter
              customer={data.item.snapshot.customer}
              badOpen={badOpen}
              onBadOpenChange={setBadOpen}
              onGood={() => decide({ decision: "GOOD" })}
              onSaveBad={onSaveBad}
              // Once a HARD clock reads 0:00 the item belongs to the timeout, not
              // to a late click: the server would record it as a timeout anyway
              // (M1), so the footer stops inviting one (L8).
              saving={decideM.isPending || timeoutM.isPending || hardClockOut || advanced}
            />
          )}
        </div>
        {railPhase && (
        <div>
          {/* Spacer, outside the sticky block: at rest the progress strip lines
              up with the hero card; once stuck the pane pins to the top. The
              column must stay stretched (no `alignItems: start` on the grid) or
              the sticky block has no travel and never pins — AUDIT-TraineeUI M1. */}
          <div aria-hidden="true" data-testid="train-run-rail-offset" style={{ height: align.offset }} />
          <div style={{ position: "sticky", top: 12, maxHeight: "calc(100vh - 24px)", overflowY: "auto", paddingBottom: 4 }} data-testid="train-run-rail-pane">
            <QuestionRail
              questions={questions}
              skipped={skipped}
              activeId={active.id}
              onActiveChange={setActiveId}
              drafts={drafts}
              onDraftChange={onDraftChange}
              graded={graded}
              onBack={onBack}
              canBack={(history[active.id]?.length ?? 0) > 0}
              onCheck={onCheck}
              checking={answerM.isPending || previewM.isPending}
              onNext={onNext}
              advancing={decideM.isPending || advanced}
              evidence={evidence}
              completionNoteElementId={COMPLETION_NOTE_ELEMENT_ID}
              deliveryDetailsElementId={DELIVERY_DETAILS_ELEMENT_ID}
              phase={phase}
              stripMinHeight={align.heroHeight || undefined}
              finalLabel={preview ? "Finish preview" : isLastItem ? "Finish phase ›" : "Next delivery ›"}
              silent={!instantFeedback}
              submitted={submitted}
            />
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function inert(color: string, bg: string): React.CSSProperties {
  return {
    padding: "4px 10px", borderRadius: 6, fontSize: 11.5, fontWeight: 700, fontFamily: Starling.fontBody, cursor: "not-allowed", opacity: 0.6,
    border: `1px solid ${color === "#fff" ? bg : color}`, background: bg, color,
  };
}
